import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { workspace } from '@lawbee/domain';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ErrorState,
  LoadingState,
  ThemeToggle,
  TopNavLayout,
  useToast,
  type SidebarNavItem,
} from '@lawbee/ui';
import {
  Building2,
  CalendarDays,
  Check,
  ChevronsUpDown,
  ClipboardList,
  CreditCard,
  FolderOpen,
  LayoutDashboard,
  LayoutGrid,
  BellRing,
  LogOut,
  Megaphone,
  Settings,
  ShieldCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { PlanBanner } from '../components/PlanGate';
import { api, errorMessage } from '../lib/api';
import { ME_KEY, useMeQuery } from '../lib/me';
import type { Me } from '../lib/types';
import { Logo, PRODUCT_NAME } from './Logo';

type NavDef = { key: string; label: string; to: string; icon: ReactNode; match: (p: string) => boolean };

const NAV: NavDef[] = [
  { key: 'dashboard', label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard />, match: (p) => p.startsWith('/dashboard') },
  { key: 'intakes', label: 'Intakes', to: '/intakes', icon: <ClipboardList />, match: (p) => p.startsWith('/intakes') || p.startsWith('/intake-forms') },
  { key: 'matters', label: 'Matters', to: '/matters', icon: <FolderOpen />, match: (p) => p.startsWith('/matters') },
  { key: 'updates', label: 'Updates', to: '/updates', icon: <Megaphone />, match: (p) => p.startsWith('/updates') },
  { key: 'bookings', label: 'Bookings', to: '/bookings', icon: <CalendarDays />, match: (p) => p.startsWith('/bookings') },
  { key: 'payments', label: 'Payments', to: '/payments', icon: <CreditCard />, match: (p) => p.startsWith('/payments') },
  { key: 'checkins', label: 'Check-ins', to: '/checkins', icon: <BellRing />, match: (p) => p.startsWith('/checkins') },
  { key: 'settings', label: 'Settings', to: '/settings', icon: <Settings />, match: (p) => p.startsWith('/settings') },
];

export function AppLayout() {
  const me = useMeQuery();
  if (me.isLoading) return <LoadingState label="Loading your workspace" className="min-h-dvh" />;
  if (me.error) return <ErrorState className="m-8" error={me.error} onRetry={() => void me.refetch()} />;
  if (!me.data) return <LoadingState label="Signing in" className="min-h-dvh" />;
  return <Shell me={me.data} />;
}

function Shell({ me }: { me: Me }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav: SidebarNavItem[] = NAV.map((item) => ({
    key: item.key,
    label: item.label,
    icon: item.icon,
    active: item.match(pathname),
    render: ({ className, children, onNavigate }) => (
      <Link to={item.to} className={className} onClick={onNavigate}>
        {children}
      </Link>
    ),
  }));

  return (
    <TopNavLayout
      brand={<WorkspaceSwitcher me={me} />}
      nav={nav}
      actions={
        <>
          {me.workspace.managedBy === 'hive' && me.hiveWebUrl ? (
            <a
              href={`${me.hiveWebUrl.replace(/\/$/, '')}/apps`}
              className="hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground sm:inline-flex"
            >
              <LayoutGrid className="size-4" aria-hidden /> All apps
            </a>
          ) : null}
          <ThemeToggle />
          <UserMenu me={me} />
        </>
      }
    >
      <PlanBanner me={me} />
      <Outlet />
    </TopNavLayout>
  );
}

function WorkspaceSwitcher({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const several = me.workspaces.length > 1;

  const switchTo = async (id: string) => {
    if (id === me.workspace.id) return;
    try {
      const res = await api.post<{ ok: boolean; me: Me }>('/api/auth/switch', { workspaceId: id });
      qc.clear();
      qc.setQueryData(ME_KEY, res.me);
      await navigate({ to: '/dashboard' });
    } catch (err) {
      toast.error('Could not switch workspace', errorMessage(err));
    }
  };

  const brand = (
    <span className="flex max-w-56 items-center gap-2.5 rounded-lg py-1.5 pl-1 pr-2 text-left">
      <Logo className="size-7 shrink-0" label="" />
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-[15px] font-semibold">{me.workspace.name}</span>
        <span className="text-[11px] font-medium text-muted-foreground">{PRODUCT_NAME}</span>
      </span>
      {several ? <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden /> : null}
    </span>
  );
  if (!several) {
    return (
      <Link to="/dashboard" aria-label={`${me.workspace.name} dashboard`}>
        {brand}
      </Link>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="rounded-lg transition-colors hover:bg-foreground/[0.04]" aria-label="Switch workspace">
          {brand}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        {me.workspaces.map((w) => (
          <DropdownMenuItem key={w.id} icon={<Building2 />} onSelect={() => void switchTo(w.id)}>
            <span className="flex-1 truncate">{w.name}</span>
            <span className="text-xs text-muted-foreground">{workspace.ROLE_LABELS[w.role]}</span>
            {w.id === me.workspace.id ? <Check className="!text-accent-foreground" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const signOut = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // already signed out
    }
    qc.clear();
    qc.setQueryData(ME_KEY, null);
    await navigate({ to: '/login' });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Account: ${me.user.name}`}
          className="rounded-full transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30"
        >
          <Avatar name={me.user.name} size="md" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="normal-case">
          <span className="block truncate text-[14px] font-semibold text-foreground">{me.user.name}</span>
          <span className="block truncate font-normal">{me.user.email}</span>
          <span className="mt-1 block font-normal">
            {workspace.ROLE_LABELS[me.role]} · {me.entitlements.planName}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<Settings />} onSelect={() => void navigate({ to: '/settings/firm' })}>
          Firm settings
        </DropdownMenuItem>
        {me.permissions.includes('audit') ? (
          <DropdownMenuItem icon={<ShieldCheck />} onSelect={() => void navigate({ to: '/settings/audit' })}>
            Audit log
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<LogOut />} onSelect={() => void signOut()}>
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
